import React from "react";
import { FormattedMessage } from "react-intl";
import { Box, Typography, Paper, Chip } from "@mui/material";
import { AccountTree } from "@mui/icons-material";
import {
  SimpleDialog,
  LoadingIndicator,
} from "../../../toolympus/components/primitives";
import { FormControlsForFields } from "../../../toolympus/components/schemed/FormControlsForFields";
import { EditItemProps } from "../../../toolympus/api/useNewItem";
import { CopTreeNode } from "./useCopTreeNodes";
import {
  CopDocumentNodeLink,
  CopDocumentNodeLinkSchema,
} from "./useCopDocumentNodeLinks";

interface Props
  extends Pick<
    EditItemProps<CopDocumentNodeLink>,
    "item" | "update" | "errors" | "save" | "isLoading"
  > {
  isOpen: boolean;
  close: () => void;
  nodesById: Map<number, CopTreeNode>;
}

export const EditDocumentNodeLinkDialog: React.FC<Props> = ({
  isOpen,
  close,
  item,
  update,
  save,
  isLoading,
  nodesById,
}) => {
  const node = item ? nodesById.get(item.node_id) : null;

  const getNodePathBreadcrumb = (path?: string | null) => {
    if (!path) return "Root";
    return path
      .split(".")
      .map(id => nodesById.get(Number(id))?.title || id)
      .join(" / ");
  };

  return (
    <SimpleDialog
      dialogTitle={
        <FormattedMessage
          id="cop_tree.edit_link_title"
          defaultMessage="Edit Tree Node Link"
        />
      }
      isOpen={isOpen}
      close={close}
      save={save}
      titleActions={
        isLoading ? <LoadingIndicator sizeVariant="s" color="primary" /> : undefined
      }
      maxWidth="sm"
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Target Node Display */}
        {node && (
          <Paper
            variant="outlined"
            sx={{
              p: 1.5,
              backgroundColor: "action.hover",
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AccountTree fontSize="small" color="primary" />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {node.title}
              </Typography>
              {node.type && (
                <Chip label={node.type} size="small" sx={{ height: 18 }} />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary">
              Path: {getNodePathBreadcrumb(node.path)}
            </Typography>
          </Paper>
        )}

        {/* Link Form Fields */}
        <FormControlsForFields
          fields={[
            ["relation_type"]
          ]}
          schema={CopDocumentNodeLinkSchema}
          data={item || {}}
          onChange={(_orig, changes) => update(changes)}
        />
      </Box>
    </SimpleDialog>
  );
};